import {
  isContentBlockFormulaNumberFormat,
  normaliseContentBlockImageSrcForStorage,
  validateContentBlockFormulaExpression,
} from "@hellocashflow/domain";
import DOMPurify from "dompurify";

import { LinkTypeEnum } from "./insertLinkDlg";

const CONTENT_BLOCK_ALLOWED_TAGS = [
  "h2",
  "h3",
  "h4",
  "h5",
  "p",
  "ul",
  "ol",
  "li",
  "blockquote",
  "hr",
  "a",
  "img",
  "span",
  "strong",
  "br",
];
const CONTENT_BLOCK_ALLOWED_ATTRIBUTES = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "data-formula",
  "data-number-format",
  "data-abs",
];
const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);

const hasSafeLinkProtocol = (href: string): boolean => {
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith("//")) return false;
  const protocol = trimmed.match(/^([a-z][a-z0-9+.-]*:)/i)?.[1];
  return !protocol || SAFE_LINK_PROTOCOLS.has(protocol.toLowerCase());
};

const normaliseLinks = (root: DocumentFragment): void => {
  for (const link of root.querySelectorAll("a")) {
    const href = link.getAttribute("href");
    if (href && !hasSafeLinkProtocol(href)) link.removeAttribute("href");

    if (link.getAttribute("target") !== "_blank") {
      link.removeAttribute("target");
      link.removeAttribute("rel");
      continue;
    }

    const relTokens = new Set(
      (link.getAttribute("rel") ?? "")
        .split(/\s+/)
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean),
    );
    relTokens.add("noopener");
    relTokens.add("noreferrer");
    link.setAttribute("rel", [...relTokens].join(" "));
  }
};

const normaliseImages = (root: DocumentFragment): void => {
  for (const image of root.querySelectorAll("img")) {
    const normalised = normaliseContentBlockImageSrcForStorage(
      image.getAttribute("src") ?? undefined,
    );
    if (!normalised.src) image.remove();
    else image.setAttribute("src", normalised.src);
  }
};

const normaliseFormulaSpans = (root: DocumentFragment): void => {
  for (const span of root.querySelectorAll("span")) {
    const isFormulaCandidate =
      span.hasAttribute("data-formula") ||
      span.hasAttribute("data-number-format") ||
      span.hasAttribute("data-abs");
    if (!isFormulaCandidate) {
      span.replaceWith(...Array.from(span.childNodes));
      continue;
    }

    const formula = span.getAttribute("data-formula")?.trim() ?? "";
    const numberFormat = span.getAttribute("data-number-format")?.trim() ?? "";
    const numberFormatIsValid = isContentBlockFormulaNumberFormat(numberFormat);
    const formulaIsValid =
      numberFormatIsValid &&
      validateContentBlockFormulaExpression(formula, numberFormat).ok;
    const isEmpty = !span.textContent.trim() && span.children.length === 0;
    if (!formulaIsValid || !isEmpty) {
      span.remove();
      continue;
    }

    span.setAttribute("data-formula", formula);
    span.setAttribute("data-number-format", numberFormat);
    span.setAttribute(
      "data-abs",
      span.getAttribute("data-abs") === "true" ? "true" : "false",
    );
  }
};

/**
 * Browser-side defense before sending Admin-authored help HTML to the API.
 *
 * @remarks The API repeats the canonical server-only sanitization before storage;
 * this browser-safe pass keeps the editor WYSIWYG contract without importing Node
 * sanitizer dependencies into either frontend bundle.
 * @see docs/guides/HOVER_HELP.md
 */
export const sanitizeEditableContentBlockHtmlForAdminSave = (
  html: string,
): string => {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_ATTR: CONTENT_BLOCK_ALLOWED_ATTRIBUTES,
    ALLOWED_TAGS: CONTENT_BLOCK_ALLOWED_TAGS,
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["script", "style"],
    RETURN_TRUSTED_TYPE: false,
  });
  const template = document.createElement("template");
  template.innerHTML = sanitized;
  normaliseLinks(template.content);
  normaliseImages(template.content);
  normaliseFormulaSpans(template.content);
  return template.innerHTML;
};

const escapeHtmlText = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeHtmlAttribute = escapeHtmlText;

export const buildEditableContentBlockLinkHtml = (
  linkType: LinkTypeEnum,
  url: string,
  displayText: string | undefined,
): string => {
  const safeUrl = escapeHtmlAttribute(url.trim());
  const safeText = escapeHtmlText(displayText ?? url);

  switch (linkType) {
    case LinkTypeEnum.WebpageNewWindow:
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
    case LinkTypeEnum.WebpageSameWindow:
      return `<a href="${safeUrl}">${safeText}</a>`;
    case LinkTypeEnum.Email:
      return `<a href="mailto:${safeUrl}">${safeText}</a>`;
    default:
      return `<a href="${safeUrl}">${safeText}</a>`;
  }
};
